import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosInstance } from '../helpers'
import {
    DocumentShareMode,
    DocumentShareState,
    ShareableTenant,
} from '../models/Company/types'

const SHAREABLE_TENANTS_KEY = ['shareableTenants']
const DOCUMENT_SHARES_KEY = ['documentShares']

const SHAREABLE_TENANTS_STALE_MS = 60 * 1000

export interface UpdateDocumentShareInput {
    apartment_id: number
    share_mode: DocumentShareMode
    tenant_ids?: number[]
}

export const useCompanyDocumentShares = (
    documentId: number | null,
    apartmentId: number | null,
    enabled = true,
) => {
    const queryClient = useQueryClient()
    const queriesEnabled = enabled && apartmentId != null

    const shareStateQuery = useQuery<DocumentShareState, Error>({
        queryKey: [...DOCUMENT_SHARES_KEY, documentId, apartmentId],
        queryFn: async () => {
            const res = await axiosInstance.get<DocumentShareState>(
                `/company/company_document/${documentId}/shares/`,
                { params: { apartment_id: apartmentId } },
            )
            return res.data
        },
        enabled: queriesEnabled && documentId != null,
    })

    const shareableTenantsQuery = useQuery<ShareableTenant[], Error>({
        queryKey: [...SHAREABLE_TENANTS_KEY, apartmentId],
        queryFn: async () => {
            const res = await axiosInstance.get<ShareableTenant[]>(
                '/lease/shareable_tenants/',
                { params: { apartment_id: apartmentId } },
            )
            return res.data
        },
        enabled: queriesEnabled,
        staleTime: SHAREABLE_TENANTS_STALE_MS,
    })

    const updateShareState = useMutation({
        mutationFn: async (input: UpdateDocumentShareInput) => {
            const res = await axiosInstance.put<DocumentShareState>(
                `/company/company_document/${documentId}/shares/`,
                input,
            )
            return res.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: DOCUMENT_SHARES_KEY })
            queryClient.invalidateQueries({ queryKey: ['companyDocuments'] })
        },
    })

    const loadError = shareStateQuery.error ?? shareableTenantsQuery.error

    return {
        shareState: shareStateQuery.data,
        isLoadingShareState: shareStateQuery.isLoading,
        shareableTenants: shareableTenantsQuery.data ?? [],
        isLoadingShareableTenants: shareableTenantsQuery.isLoading,
        loadError,
        updateShareState,
        isUpdatingShareState: updateShareState.isLoading,
        refetchShareState: shareStateQuery.refetch,
    }
}
